import { CrmFieldResponse, LookupResponse, PicklistResponse } from 'common/interfaces';

import type { IncomingInvoiceType } from './constants';

export interface IncomingInvoiceAttributes {
  createdon: PicklistResponse<string, string>;
  new_sparteid: CrmFieldResponse<LookupResponse>;
  new_status_bearbeitung: CrmFieldResponse<PicklistResponse<string, number>>;
  new_summerechnungsbetragnotax: CrmFieldResponse<PicklistResponse<string, number>>;
  uds_bemerkungkunde: CrmFieldResponse;
  uds_creditor_name: CrmFieldResponse;
  uds_datensatzeditedon: CrmFieldResponse<PicklistResponse<string, string>>;
  uds_datensatzeditorid: CrmFieldResponse<LookupResponse>;
  uds_eingangsrechnungid: string;
  uds_incidentid: CrmFieldResponse<LookupResponse>;
  uds_invoice_typecode: CrmFieldResponse<PicklistResponse<string, IncomingInvoiceType>>;
  uds_invoicedate: CrmFieldResponse<PicklistResponse<string, string>>;
  uds_invoicenumber: CrmFieldResponse;
  uds_name: CrmFieldResponse;
  uds_rahmenvertrageid: CrmFieldResponse<LookupResponse>;
  uds_rechnungsumme_brutto: CrmFieldResponse<PicklistResponse<string, number>>;
  uds_rechnungsumme_netto: CrmFieldResponse<PicklistResponse<string, number>>;
  uds_rechnungsumme_netto_gesamt: CrmFieldResponse<PicklistResponse<string, number>>;
  uds_result_validationcode: CrmFieldResponse<PicklistResponse<string, number>>;
  uds_final_invoice_leasing_returncode: CrmFieldResponse<PicklistResponse>;
  uds_sapbelegtyp: CrmFieldResponse<PicklistResponse<string, number>>;
  uds_service_date: CrmFieldResponse<PicklistResponse<string, string>>;
  uds_serviceproviderid: CrmFieldResponse<LookupResponse>;
  uds_statuskunde: CrmFieldResponse<PicklistResponse<string, number>>;
  status: CrmFieldResponse<PicklistResponse>;
  uds_statusprufungkunde: CrmFieldResponse<PicklistResponse<string, number>>;
  uds_text_errors: CrmFieldResponse<string>;
  uds_rounding_cents: CrmFieldResponse;
  uds_empfnger: CrmFieldResponse<PicklistResponse>;
  uds_vertraggekundigtcode: CrmFieldResponse<PicklistResponse>;
  uds_bemerkung: CrmFieldResponse;
  uploadAllowed?: boolean;
  uds_startdatum: CrmFieldResponse<PicklistResponse<string, string>>;
  uds_enddatum: CrmFieldResponse<PicklistResponse<string, string>>;
  uds_createon_day_month: CrmFieldResponse;
  uds_invoice_date_next_monthcode: CrmFieldResponse<PicklistResponse>;
  uds_einvoice_parsedon: CrmFieldResponse<PicklistResponse<string, string>>;
  uds_date_validation: CrmFieldResponse<PicklistResponse<string, string>>;
  uds_accounting_file_processed_on: CrmFieldResponse<PicklistResponse<string, string>>;
  uds_transferring_text: CrmFieldResponse;
  uds_text_onaviscode: CrmFieldResponse<PicklistResponse>;
  uds_internal_error_message: CrmFieldResponse;
  uds_dauerbeleg: CrmFieldResponse<PicklistResponse>;
}

export interface IncomingInvoiceListItem extends IncomingInvoiceAttributes {
  modifiedon?: PicklistResponse<string, string> | null;
  'serviceProvider.customertypecode'?: PicklistResponse | null;
  uds_rechnung_mwst_altteil?: PicklistResponse | null;
  uds_source?: PicklistResponse | null;
}

export interface IncomingInvoice {
  id: string;
  name: string;
  attributes: IncomingInvoiceAttributes;
}

export interface IncomingInvoiceFile {
  fileId: string;
  fileName: string;
}

export interface IncomingInvoiceWithFiles {
  eingangsrechnung: {
    attributes: IncomingInvoice;
    id: string;
    name: string;
  };
  files: IncomingInvoiceFile[];
}

export type StatusName = 'status';

export enum IncomingInvoiceFieldNames {
  CreditorLookup = 'uds_serviceproviderid',
  CreditorTextInput = 'uds_creditor_name',
  Company = 'new_sparteid',
  FrameworkAgreement = 'uds_rahmenvertrageid',
  InvoiceNumber = 'uds_invoicenumber',
  InvoiceDate = 'uds_invoicedate',
  DocumentType = 'uds_sapbelegtyp',
  FinalInvoiceLeasingReturnCode = 'uds_final_invoice_leasing_returncode',
  ServiceDate = 'uds_service_date',
  RoundingCents = 'uds_rounding_cents',
  ReceiptRecipient = 'uds_empfnger',
  ContractTerminated = 'uds_vertraggekundigtcode',
  ProcessingStatus = 'new_status_bearbeitung',
  CustomerStatus = 'uds_statuskunde',
  CustomerStatusCheck = 'uds_statusprufungkunde',
  Remark = 'uds_bemerkung',
  CustomerComment = 'uds_bemerkungkunde',
  InvoiceTypeCode = 'uds_invoice_typecode',
  IncidentNumber = 'uds_incidentid',
  PermanentReceiptEnabled = 'uds_dauerbeleg',
  PermanentReceiptStartDate = 'uds_startdatum',
  PermanentReceiptEndDate = 'uds_enddatum',
  CreateOnDayMonth = 'uds_createon_day_month',
  InvoiceDateNextMonth = 'uds_invoice_date_next_monthcode',
  EInvoiceParsedOn = 'uds_einvoice_parsedon',
  TextErrors = 'uds_text_errors',
  DateValidation = 'uds_date_validation',
  ResultValidation = 'uds_result_validationcode',
  AccountingFileProcessedOn = 'uds_accounting_file_processed_on',
  InternalErrorMessage = 'uds_internal_error_message',
  SapTextHandover = 'uds_transferring_text',
  TextOnAvis = 'uds_text_onaviscode',
  Files = 'files'
}

export enum SplitBookingFieldNames {
  Vehicle = 'uds_fuhrparkid',
  LedgerAccount = 'uds_sachkontoid',
  TaxCode = 'uds_mwst',
  CostCenter = 'uds_kostenstelle',
  PerformanceDate = 'uds_leistungdatum',
  NetAmount = 'netto'
}

export type SplitBookingFormFieldValuesType = Record<SplitBookingFieldNames, string> & Record<string, string | null>;

export type IncomingInvoiceFormFieldValuesType = Omit<
  Record<IncomingInvoiceFieldNames, string>,
  IncomingInvoiceFieldNames.Files | IncomingInvoiceFieldNames.PermanentReceiptEnabled
> & {
  [IncomingInvoiceFieldNames.Files]: File[];
  [IncomingInvoiceFieldNames.PermanentReceiptEnabled]: boolean;
} & SplitBookingFormFieldValuesType;

export enum DuplicateCheckType {
  CreditorNumber = 1,
  DateNumber = 2
}
